/**
 * Contracts tab (spec §9.3) and Saves tab (spec §10, §11).
 */
import type { ReactNode } from 'react'
import { FocusButton } from '../focus'
import { ACTIVE_COLS, AUTO_COLS, G, MANUAL_COLS } from '../models/layout'
import type { ContractsModel } from '../models/contracts'
import type { SaveRow, SavesModel } from '../models/saves'
import type { ManualSlotId } from '../contracts'
import type { ScreenProps } from './common'

export function ContractsTab(p: ScreenProps & { model: ContractsModel }): ReactNode {
  const { model, ctl } = p
  return (
    <div className="ui-tab ui-contracts">
      <section className="ui-section">
        <h3 className="ui-section__title">Board</h3>
        {!model.available ? <p className="ui-empty">No contract board here.</p> : null}
        {model.boardLine !== null ? (
          <p className="ui-seed" data-testid="contracts-board-line">
            <span className="ui-seed__speaker">{model.boardLine.speaker}:</span> {model.boardLine.text}
          </p>
        ) : null}
        {model.available && model.board.length === 0 ? <p className="ui-empty">No jobs left on the board this cycle.</p> : null}
        {model.board.map((row, i) => (
          <FocusButton key={row.id} group={G.board} index={i} testId={`contract-accept-${row.id}`} className="ui-row ui-row--contract" onActivate={() => ctl.run(() => ctl.actions.acceptContract(row.id))}>
            <span className="ui-row__main">
              <span className="ui-row__title">
                {row.typeLabel}
                {row.premium ? (
                  <span className="ui-tag" data-kind="premium">
                    {'★'} {'×'}1.5
                  </span>
                ) : null}
              </span>
              <span className="ui-row__sub">{row.requirement}</span>
              <span className="ui-row__sub">
                Deadline {row.deadline} {'·'} standing {row.standing}
              </span>
            </span>
            <span className="ui-row__price">{row.reward}</span>
            <span className="ui-row__cta">Accept</span>
          </FocusButton>
        ))}
      </section>
      <section className="ui-section">
        <h3 className="ui-section__title">Active jobs</h3>
        {model.active.length === 0 ? <p className="ui-empty">No active jobs.</p> : null}
        {model.active.map((row, i) => (
          <div key={row.id} className="ui-row ui-row--job" data-ready={row.ready ? '' : undefined}>
            <span className="ui-row__main">
              <span className="ui-row__title">
                {row.typeLabel} for {row.station}
                {row.ready ? <span className="ui-tag" data-kind="ready">{'✓'} Ready</span> : null}
              </span>
              <span className="ui-row__sub">{row.requirement}</span>
              <span className="ui-row__sub">
                {row.reward} {'·'} {row.timeLeft}
                {row.handInNote !== null ? ` · ${row.handInNote}` : ''}
              </span>
            </span>
            <FocusButton group={G.active} index={i * ACTIVE_COLS} testId={`contract-handin-${row.id}`} className="ui-btn--small" disabled={!row.canHandIn} onActivate={() => ctl.run(() => ctl.actions.handInContract(row.id))}>
              Hand in
            </FocusButton>
            <FocusButton group={G.active} index={i * ACTIVE_COLS + 1} testId={`contract-abandon-${row.id}`} className="ui-btn--small ui-btn--quiet" onActivate={() => ctl.run(() => ctl.actions.abandonContract(row.id))}>
              Abandon
            </FocusButton>
          </div>
        ))}
      </section>
    </div>
  )
}

function SlotLine(p: ScreenProps & { row: SaveRow; group: string; base: number; save: boolean }): ReactNode {
  const { row, ctl } = p
  const first = p.base + (p.save ? 1 : 0)
  return (
    <div className="ui-row ui-row--save" data-status={row.status}>
      <span className="ui-row__main">
        <span className="ui-row__title">
          {row.label}
          {row.error !== null ? <span className="ui-tag" data-kind="error">{'⚠'} {row.status === 'future' ? 'Newer version' : 'Corrupt'}</span> : null}
        </span>
        <span className="ui-row__sub">
          {row.summary}
          {row.savedAt !== '' ? ` · saved ${row.savedAt}` : ''}
        </span>
        {row.error !== null ? <span className="ui-row__sub ui-error-text">{row.error}</span> : null}
      </span>
      {p.save ? (
        <FocusButton group={p.group} index={p.base} testId={`saves-save-${row.slot}`} className="ui-btn--small" disabled={!row.canSave} onActivate={() => ctl.runAsync(() => ctl.actions.saveTo(row.slot as ManualSlotId))}>
          Save
        </FocusButton>
      ) : null}
      <FocusButton group={p.group} index={first} testId={`saves-load-${row.slot}`} className="ui-btn--small" disabled={!row.canLoad} onActivate={() => ctl.runAsync(() => ctl.actions.loadSlot(row.slot))}>
        Load
      </FocusButton>
      <FocusButton group={p.group} index={first + 1} testId={`saves-export-${row.slot}`} className="ui-btn--small ui-btn--quiet" disabled={!row.canExport} onActivate={() => ctl.exportSlot(row.slot)}>
        Export
      </FocusButton>
    </div>
  )
}

export function SavesTab(p: ScreenProps & { model: SavesModel }): ReactNode {
  const { model, ctl } = p
  return (
    <div className="ui-tab ui-saves">
      {model.storageBanner !== null ? <p className="ui-banner" role="status">{'⚠'} {model.storageBanner}</p> : null}
      <section className="ui-section">
        <h3 className="ui-section__title">Manual saves</h3>
        {model.manual.map((row, i) => (
          <SlotLine key={row.slot} ctl={ctl} row={row} group={G.manual} base={i * MANUAL_COLS} save />
        ))}
      </section>
      <section className="ui-section">
        <h3 className="ui-section__title">Autosaves</h3>
        {model.auto.map((row, i) => (
          <SlotLine key={row.slot} ctl={ctl} row={row} group={G.auto} base={i * AUTO_COLS} save={false} />
        ))}
      </section>
      <div className="ui-actions">
        <FocusButton group={G.importSave} index={0} testId="saves-import" className="ui-btn--wide" onActivate={() => ctl.importFile()}>
          Import a save file{'…'}
        </FocusButton>
      </div>
    </div>
  )
}
